import {
  type ComponentPropsWithoutRef,
  type CSSProperties,
  type ReactNode,
  useMemo
} from 'react'
import { useSnapshot } from 'valtio'

import { formatRelease } from '@/lib/format'
import { state } from '@/lib/state'
import { cn } from '@/lib/utils'
import { Flag } from '../../../../types'
import styles from './Badge.module.css'

interface BadgeProps extends ComponentPropsWithoutRef<'span'> {
  children?: ReactNode
  color?: string
  selectable?: boolean
  selected?: boolean
  size?: 'small' | 'medium' | 'large'
  'data-testid'?: string
}

export function Badge({
  children,
  color,
  size = 'medium',
  selectable,
  selected,
  'data-testid': testId,
  ...props
}: BadgeProps) {
  const style = useMemo(
    () => (color ? ({ '--badge': color } as CSSProperties) : undefined),
    [color]
  )

  const Component = props.onClick ? 'button' : 'span'

  return (
    <Component
      {...props}
      style={style}
      className={cn(
        props.className,
        styles.badge,
        styles[`size-${size}`],
        color && styles.colored,
        selectable && styles.selectable,
        selected && styles.selected
      )}
      data-testid={`badge-${testId || 'default'}`}
    >
      {children}
    </Component>
  )
}

interface RingBadgeProps extends Omit<BadgeProps, 'color' | 'children'> {
  ring: string
  release?: string
}

export function RingBadge({
  ring: ringName,
  release,
  ...props
}: RingBadgeProps) {
  const { radarConfig } = useSnapshot(state)
  const rings = radarConfig?.rings || []
  const ringData = rings.find(r => r.id === ringName)
  if (!ringData) return null

  const label = release
    ? `${ringData.title} | ${formatRelease(release)}`
    : ringData.title

  return (
    <Badge
      color={ringData.color}
      {...props}
      data-testid={`badge-ring-${ringName}`}
    >
      {label}
    </Badge>
  )
}

// Type guard to check if flag has the required attributes
// biome-ignore lint/suspicious/noExplicitAny: <explanation>
function hasRequiredFlagAttributes(flag: any): flag is {
  color: string
  title: string
  titleShort: string
} {
  return 'color' in flag && 'title' in flag && 'titleShort' in flag
}

interface FlagBadgeProps
  extends Omit<BadgeProps, 'color' | 'children' | 'size'> {
  flag: Flag
  short?: boolean
  outlined?: boolean
}

export function FlagBadge({
  flag: flagName,
  short,
  outlined,
  ...props
}: FlagBadgeProps) {
  const { radarConfig } = useSnapshot(state)
  const flags = radarConfig?.flags || {}
  if (flagName === Flag.Default) return null
  const flag = flags[flagName]
  if (!flag || !hasRequiredFlagAttributes(flag)) return null

  return (
    <Badge
      color={flag.color}
      size="small"
      {...props}
      className={cn(props.className, outlined && styles.outlined)}
      data-testid={`badge-flag-${flagName}`}
    >
      {short ? flag.titleShort : flag.title}
    </Badge>
  )
}
